/**
 * AuthenticatedApiClient — Centralized module for making authenticated API requests.
 *
 * Replaces the duplicated `apiRequest()` methods across all service classes.
 * Handles JWT token retrieval via fetchAuthSession, attaches Authorization: Bearer header,
 * and implements 401 silent retry with token refresh.
 *
 * In dev bypass mode (no VITE_COGNITO_USER_POOL_ID / VITE_COGNITO_CLIENT_ID),
 * requests are sent without an Authorization header and fetchAuthSession is never called.
 *
 * @module authenticated-api-client
 * @see Requirements 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 9.2, 9.3, 10.2
 */

const isCognitoConfigured = !!import.meta.env.VITE_COGNITO_USER_POOL_ID &&
    !!import.meta.env.VITE_COGNITO_CLIENT_ID;

const isPreviewMode = import.meta.env.VITE_PREVIEW_MODE === 'true';

const DEFAULT_TIMEOUT_MS = 30_000;
const MAX_RETRIES = 2;
const RETRY_BASE_MS = 1000;

function isRetryableStatus(status: number): boolean {
    return status === 429 || status >= 500;
}

async function fetchWithTimeout(url: string, options: RequestInit, timeoutMs: number): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await fetch(url, { ...options, signal: controller.signal });
    } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
            throw new Error(`Request timed out after ${timeoutMs}ms`);
        }
        throw err;
    } finally {
        clearTimeout(timer);
    }
}

export class AuthenticatedApiClient {
    private baseUrl: string;
    private timeoutMs: number;

    constructor(baseUrl?: string, timeoutMs?: number) {
        const defaultUrl = import.meta.env.DEV
            ? '/api'
            : (import.meta.env.VITE_API_GATEWAY_URL || '');
        this.baseUrl = (baseUrl || defaultUrl).replace(/\/+$/, '');
        this.timeoutMs = timeoutMs ?? DEFAULT_TIMEOUT_MS;
    }

    async apiRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
        if (isPreviewMode) {
            throw new Error('API unavailable in preview mode');
        }
        let headers = { ...options.headers } as Record<string, string>;

        if (isCognitoConfigured) {
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            if (!session.tokens?.idToken) {
                throw new Error('No authentication token available. Please sign in again.');
            }
            headers['Authorization'] = `Bearer ${session.tokens.idToken.toString()}`;
        }

        const url = `${this.baseUrl}${endpoint}`;
        let response = await fetchWithTimeout(url, { ...options, headers }, this.timeoutMs);

        if (response.status === 401 && isCognitoConfigured) {
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const refreshed = await fetchAuthSession({ forceRefresh: true });
            if (refreshed.tokens?.idToken) {
                headers['Authorization'] = `Bearer ${refreshed.tokens.idToken.toString()}`;
                const retryResponse = await fetchWithTimeout(url, { ...options, headers }, this.timeoutMs);
                if (!retryResponse.ok) {
                    let errorText = '';
                    try { errorText = await retryResponse.text(); } catch { /* body unreadable */ }
                    throw new Error(`API request failed: ${retryResponse.status} ${retryResponse.statusText}\n${errorText}`);
                }
                return retryResponse;
            }
            throw new Error('Session expired. Please sign in again.');
        }

        if (isRetryableStatus(response.status)) {
            for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
                await new Promise(r => setTimeout(r, RETRY_BASE_MS * 2 ** attempt));
                response = await fetchWithTimeout(url, { ...options, headers }, this.timeoutMs);
                if (!isRetryableStatus(response.status)) break;
            }
        }

        if (!response.ok) {
            let errorText = '';
            try { errorText = await response.text(); } catch { /* body unreadable */ }
            throw new Error(`API request failed: ${response.status} ${response.statusText}\n${errorText}`);
        }

        return response;
    }

    /**
     * Make a public (unauthenticated) API request — no Authorization header attached.
     * Use for endpoints that don't require sign-in (market browsing, public blog posts).
     */
    async publicRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
        if (isPreviewMode) {
            throw new Error('API unavailable in preview mode');
        }
        const url = `${this.baseUrl}${endpoint}`;
        const response = await fetchWithTimeout(url, { ...options }, this.timeoutMs);

        if (!response.ok) {
            let errorText = '';
            try { errorText = await response.text(); } catch { /* body unreadable */ }
            throw new Error(`API request failed: ${response.status} ${response.statusText}\n${errorText}`);
        }

        return response;
    }
}
